/**
 * Change Status Modal Component
 * @description 티켓 상태 변경 모달
 */

import { useState } from 'react';
import {
  Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle, Label, Select, SelectContent, SelectItem, SelectTrigger,
  SelectValue, Textarea
} from '@shared/ui/primitives';
import type { TicketStatus } from '@/types/ticket.types';

interface ChangeStatusModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (status: TicketStatus, comment?: string) => Promise<void>;
    currentStatus: TicketStatus;
}

const statusLabels: Record<TicketStatus, string> = {
    draft: '임시저장',
    submitted: '제출됨',
    in_progress: '진행중',
    pending: '대기중',
    resolved: '해결됨',
    closed: '완료',
    rejected: '거부됨',
    cancelled: '취소됨',
};

export const ChangeStatusModal: React.FC<ChangeStatusModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    currentStatus,
}) => {
    const [newStatus, setNewStatus] = useState<TicketStatus>(currentStatus);
    const [comment, setComment] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleConfirm = async () => {
        if (newStatus === currentStatus) {
            onClose();
            return;
        }

        try {
            setIsLoading(true);
            await onConfirm(newStatus, comment.trim() || undefined);
            setComment('');
            onClose();
        } catch (error) {
            console.error('Change status failed:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        if (!isLoading) {
            setNewStatus(currentStatus);
            setComment('');
            onClose();
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>상태 변경</DialogTitle>
                    <DialogDescription>
                        티켓의 상태를 변경합니다.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    <div className="space-y-2">
                        <Label htmlFor="status">새 상태</Label>
                        <Select
                            value={newStatus}
                            onValueChange={(value) => setNewStatus(value as TicketStatus)}
                        >
                            <SelectTrigger id="status">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {Object.entries(statusLabels).map(([value, label]) => (
                                    <SelectItem key={value} value={value}>
                                        {label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="comment">코멘트 (선택사항)</Label>
                        <Textarea
                            id="comment"
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="상태 변경 사유를 입력하세요"
                            rows={4}
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={handleClose}
                        disabled={isLoading}
                    >
                        취소
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        disabled={isLoading || newStatus === currentStatus}
                    >
                        {isLoading ? '처리 중...' : '변경'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

ChangeStatusModal.displayName = 'ChangeStatusModal';
